import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Button,
  Card,
  Table,
  Space,
  Typography,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Tag,
  Tabs,
  Select,
  Switch,
  Divider,
  Row,
  Col,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  SearchOutlined,
  CloseOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { Editor } from '@monaco-editor/react';
import { apiService } from '../services/api';
import {
  LandingTemplate,
  LandingFile,
  PublicEndpoint,
  StaticSite,
} from "../types/api";
import { useTheme } from '../contexts/ThemeContext';
import { FileManager } from '../components/FileManager';

const { Title, Text } = Typography;

const safeParseJSON = <T,>(input: unknown, fallback: T): T => {
  if (Array.isArray(input)) return input as T;
  if (typeof input === "string") {
    try {
      return JSON.parse(input) as T;
    } catch {
      return fallback;
    }
  }
  return fallback;
};

const normalizeTemplateFromApi = (raw: any) => {
  const cfg = raw?.config ?? {};
  return {
    ...raw,
    // переносим полезное из config наверх, как ожидает фронт
    version: cfg.version ?? raw.version,
    macros: safeParseJSON(cfg.macros, [] as any[]),
    publicEndpoints: safeParseJSON(cfg.publicEndpoints, [] as any[]),
  };
};

export const Landings: React.FC = () => {
  const { monacoTheme } = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  
  // Визначаємо режим на основі URL
  const isCreateMode = location.pathname.includes('/create');
  const isEditMode = location.pathname.includes('/edit') && !!id;
  const isFormMode = isCreateMode || isEditMode;
  
  // Основні стани
  const [loading, setLoading] = useState(false);
  
  // Landing Templates
  const [landingTemplates, setLandingTemplates] = useState<LandingTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<LandingTemplate | null>(null);
  const [isTemplateModalVisible, setIsTemplateModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<LandingTemplate | null>(null);
  const [templateForm] = Form.useForm();
  
  // Macros and Public Endpoints state
	const [publicEndpoints, setPublicEndpoints] = useState<PublicEndpoint[]>([]);
	
	// Static sites
	const [staticSites, setStaticSites] = useState<StaticSite[]>([]);
  
  // File Editor (окремо від файлового менеджера)
  const [selectedFile, setSelectedFile] = useState<LandingFile | null>(null);
  const [fileContent, setFileContent] = useState<string>('');
  const [isFileEditorVisible, setIsFileEditorVisible] = useState(false);
  
  // Пошук
	const [searchTerm, setSearchTerm] = useState('');

	const templatesWithActive = landingTemplates.map((t) => ({
    ...t,
    active: staticSites.some((s) => s.landingTemplateId === t.id),
  }));
	

  // Завантаження даних
  const loadLandingTemplates = async () => {
    try {
      setLoading(true);
			const data = await apiService.getLandingTemplates();
			const normalized = (data ?? []).map(normalizeTemplateFromApi)
      setLandingTemplates(normalized);
    } catch (error) {
      message.error('Помилка завантаження шаблонів лендингів');
    } finally {
      setLoading(false);
    }
	};
	
	// Завантаження статичних сайтів
	const loadStaticSites = async () => {
    try {
      const sites = await apiService.getStaticSites();
      setStaticSites(sites || []);
    } catch {

    }
  };

  // Обробники подій
  const handleCreateTemplate = async (values: any) => {
    try {
      const templateData = {
        ...values,
        publicEndpoints: publicEndpoints,
      };
      
      console.log('📝 Creating template with data:', templateData);
      await apiService.createLandingTemplate(templateData);
      message.success('Шаблон лендингу створено');
      navigate('/landings');
      templateForm.resetFields();
      setPublicEndpoints([]);
      await loadLandingTemplates();
    } catch (error) {
      console.error('❌ Error creating template:', error);
      message.error('Помилка створення шаблону');
    }
  };

  const handleEditTemplate = (template: LandingTemplate) => {
    navigate(`/landings/edit/${template.id}`);
  };

  const handleUpdateTemplate = async (values: any) => {
    if (!editingTemplate) return;
    
    try {
      const templateData = {
        ...values,
        publicEndpoints: publicEndpoints,
      };
      
      console.log('📝 Updating template with data:', templateData);
      await apiService.updateLandingTemplate(editingTemplate.id, templateData);
      message.success('Шаблон лендингу оновлено');
      navigate('/landings');
      templateForm.resetFields();
      setPublicEndpoints([]);
      await loadLandingTemplates();
    } catch (error) {
      console.error('❌ Error updating template:', error);
      message.error('Помилка оновлення шаблону');
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    try {
      await apiService.deleteLandingTemplate(id);
      message.success('Шаблон видалено');
      await loadLandingTemplates();
    } catch (error) {
      message.error('Помилка видалення шаблону');
    }
  };

  const handleCloneTemplate = async (id: string, name: string) => {
    try {
      await apiService.cloneLandingTemplate(id, name);
      message.success('Шаблон скопійовано');
      await loadLandingTemplates();
    } catch (error) {
      message.error('Помилка копіювання шаблону');
    }
  };



  const handleSaveFileContent = async () => {
    if (!selectedFile) return;
    
    try {
      await apiService.updateLandingFileContent(selectedFile.id, fileContent);
      message.success('Файл збережено');
			setIsFileEditorVisible(false);
			await loadLandingTemplates()
    } catch (error) {
      message.error('Помилка збереження файлу');
    }
  };

  // Допоміжні функції для публічних точок
  const addPublicEndpoint = () => {
    const newEndpoint: PublicEndpoint = {
      id: `endpoint_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: '',
      label: '',
      path: '',
      method: 'GET',
      required: false,
      description: '',
    };
    setPublicEndpoints([...publicEndpoints, newEndpoint]);
  };

  const updatePublicEndpoint = (index: number, updatedEndpoint: Partial<PublicEndpoint>) => {
    const newEndpoints = [...publicEndpoints];
    newEndpoints[index] = { ...newEndpoints[index], ...updatedEndpoint };
    setPublicEndpoints(newEndpoints);
  };

  const removePublicEndpoint = (index: number) => {
    setPublicEndpoints(publicEndpoints.filter((_, i) => i !== index));
  };

  // Сброс состояния при закрытии модального окна
  const handleCloseTemplateModal = () => {
    setIsTemplateModalVisible(false);
    templateForm.resetFields();
    setPublicEndpoints([]);
    setEditingTemplate(null);
  };

  // Фільтровані дані
  const filteredTemplates = templatesWithActive.filter(
    (template) =>
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
		loadLandingTemplates();
		loadStaticSites()
  }, []);

  // Ініціалізація форми на основі режиму
  useEffect(() => {
    if (isCreateMode) {
      setEditingTemplate(null);
      templateForm.resetFields();
      setPublicEndpoints([]);
    } else if (isEditMode && id) {
      const template = landingTemplates.find(t => t.id === id);
			if (template) {
				const normalized = normalizeTemplateFromApi(template)
        setEditingTemplate(normalized);
        templateForm.setFieldsValue({
          name: normalized.name,
          description: normalized.description,
          version: normalized.version,
          category: normalized.category,
        });
        
        // Завантаження існуючих макросів та публічних точок
        setPublicEndpoints(normalized.publicEndpoints || []);
      }
    }
  }, [isCreateMode, isEditMode, id, landingTemplates]);

  // Колонки для таблиці шаблонів
  const templateColumns = [
    {
      title: 'Назва',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: LandingTemplate) => (
        <Space direction="vertical" size="small">
          <Text strong>{text}</Text>
          <div>
          <Tag color={record.active ? 'green' : 'default'}>
            {record.active ? 'Активний' : 'Неактивний'}
          </Tag>
            {record.macros && record.macros.length > 0 && (
              <Tag color="blue" title="Кількість макросів">
                {record.macros.length} макрос{record.macros.length === 1 ? '' : (record.macros.length < 5 ? 'и' : 'ів')}
              </Tag>
            )}
            {record.publicEndpoints && record.publicEndpoints.length > 0 && (
              <Tag color="purple" title="Кількість публічних точок">
                {record.publicEndpoints.length} точк{record.publicEndpoints.length === 1 ? 'а' : (record.publicEndpoints.length < 5 ? 'и' : '')}
              </Tag>
            )}
          </div>
        </Space>
      ),
    },
    {
      title: 'Категорія',
      dataIndex: 'category',
      key: 'category',
      render: (category: string) => <Tag>{category}</Tag>,
    },
    {
      title: 'Версія',
      dataIndex: 'version',
      key: 'version',
    },
    {
      title: 'Створено',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('uk-UA'),
    },
    {
      title: 'Дії',
      key: 'actions',
      render: (_: any, record: LandingTemplate) => (
        <Space>
          <Button
            type="primary"
            size="small"
            onClick={() => setSelectedTemplate(record)}
          >
            Файли
          </Button>
          <Tooltip title="Редагувати шаблон">
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditTemplate(record)}
          />
          </Tooltip>
          <Tooltip title="Скопіювати шаблон">
          <Button
            icon={<CopyOutlined />}
            size="small"
            onClick={() => {
              const name = prompt('Назва нового шаблону:');
              if (name) handleCloneTemplate(record.id, name);
            }}
          />
          </Tooltip>
          <Popconfirm
            title="Видалити шаблон?"
            onConfirm={() => handleDeleteTemplate(record.id)}
          >
            <Tooltip title="Видалити шаблон">
            <Button icon={<DeleteOutlined />} size="small" danger />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];



  // Якщо це режим створення/редагування - показуємо форму
  if (isFormMode) {
    return (
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={2}>
            <Space>
              <Button 
                icon={<ArrowLeftOutlined />} 
                onClick={() => navigate('/landings')}
                type="text"
              />
              {isCreateMode ? 'Створити шаблон лендингу' : 'Редагувати шаблон лендингу'}
            </Space>
          </Title>
        </div>

        {/* Тут буде форма редагування/створення */}
        <Card>
          <Form
            form={templateForm}
            layout="vertical"
            onFinish={editingTemplate ? handleUpdateTemplate : handleCreateTemplate}
          >
            <Tabs defaultActiveKey="basic" items={[
              {
                key: 'basic',
                label: 'Основна інформація',
                children: (
                  <div>
                    <Form.Item
                      name="name"
                      label="Назва шаблону"
                      rules={[{ required: true, message: 'Введіть назву шаблону' }]}
                    >
                      <Input placeholder="Наприклад: Промо сторінка продукту" />
                    </Form.Item>

                    <Form.Item name="description" label="Опис">
                      <Input.TextArea rows={3} placeholder="Опис шаблону лендингу" />
                    </Form.Item>

                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item name="version" label="Версія">
                          <Input placeholder="1.0.0" defaultValue="1.0.0" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="category" label="Категорія">
                          <Input placeholder="landing" defaultValue="landing" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                ),
							},			
            ]} />

            <div style={{ textAlign: 'right', marginTop: 16 }}>
              <Space>
                <Button onClick={() => navigate('/landings')}>
                  Скасувати
                </Button>
                <Button type="primary" htmlType="submit">
                  {editingTemplate ? 'Оновити шаблон' : 'Створити шаблон'}
                </Button>
              </Space>
            </div>
          </Form>
        </Card>
      </div>
    );
  }

  // Якщо це список - показуємо таблицю
  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2}>🎨 Лендинги та статичні сайти</Title>
        <Input.Search
          placeholder="Пошук..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ width: 300 }}
          prefix={<SearchOutlined />}
        />
      </div>

      <Card>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
          <Title level={4}>Шаблони лендингів</Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/landings/create')}
          >
            Створити шаблон
          </Button>
        </div>
        
        <Table
          dataSource={filteredTemplates}
          columns={templateColumns}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>


      {/* Новий файловий менеджер */}
      {selectedTemplate && (
        <FileManager
          key={selectedTemplate.id}
          template={selectedTemplate}
          visible={true}
          onClose={() => {
            setSelectedTemplate(null);
            setSelectedFile(null);
            setFileContent('');
            setIsFileEditorVisible(false);
          }}
          onFileEdit={(file, content) => {
            setSelectedFile(file);
            setFileContent(content);
            setIsFileEditorVisible(true);
          }}
        />
      )}

      {/* Модальне вікно створення/редагування шаблону */}
      <Modal
        title={editingTemplate ? 'Редагування шаблону' : 'Створення шаблону'}
        open={isTemplateModalVisible && !isFormMode}
        onCancel={() => navigate('/landings')}
        footer={null}
        width={800}
        style={{ top: 20 }}
      >
        <Form
          form={templateForm}
          layout="vertical"
          onFinish={editingTemplate ? handleUpdateTemplate : handleCreateTemplate}
        >
          <Tabs defaultActiveKey="basic" items={[
            {
              key: 'basic',
              label: 'Основна інформація',
              children: (
                <div>
          <Form.Item
            name="name"
            label="Назва шаблону"
            rules={[{ required: true, message: 'Введіть назву шаблону' }]}
          >
            <Input placeholder="Наприклад: Промо сторінка продукту" />
          </Form.Item>

          <Form.Item name="description" label="Опис">
                    <Input.TextArea rows={3} placeholder="Опис шаблону лендингу" />
          </Form.Item>

                  <Row gutter={16}>
                    <Col span={12}>
          <Form.Item name="version" label="Версія">
            <Input placeholder="1.0.0" defaultValue="1.0.0" />
          </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="category" label="Категорія">
                        <Input placeholder="landing" defaultValue="landing" />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              ),
            },
            {
              key: 'endpoints',
              label: `Публічні точки (${publicEndpoints.length})`,
              children: (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div>
                      <Title level={5}>Публічні точки шаблону</Title>
                      <Text type="secondary">
                        Публічні точки - це ендпоінти, які можуть бути налаштовані при додаванні лендингу в додаток
                      </Text>
                    </div>
                    <Button type="primary" icon={<PlusOutlined />} onClick={addPublicEndpoint}>
                      Додати точку
                    </Button>
                  </div>

                  {publicEndpoints.length === 0 ? (
                    <Card style={{ textAlign: 'center', padding: '20px 0' }}>
                      <Text type="secondary">Поки немає публічних точок. Додайте першу точку для налаштування ендпоінтів.</Text>
                    </Card>
                  ) : (
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {publicEndpoints.map((endpoint, index) => (
                        <Card 
                          key={endpoint.id} 
                          size="small" 
                          style={{ marginBottom: 12 }}
                          title={
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span>Точка #{index + 1}: {endpoint.label || endpoint.name || 'Без назви'}</span>
                              <Button 
                                type="link" 
                                danger 
                                icon={<CloseOutlined />} 
                                onClick={() => removePublicEndpoint(index)}
                                size="small"
                              />
                            </div>
                          }
                        >
                          <Row gutter={[12, 12]}>
                            <Col span={12}>
                              <label><strong>Назва точки:</strong></label>
                              <Input
                                placeholder="webhook"
                                value={endpoint.name}
                                onChange={(e) => {
                                  let value = e.target.value;
                                  // Залишаємо як є, це назва ендпоінту, не шлях
                                  updatePublicEndpoint(index, { name: value });
                                }}
                                style={{ marginTop: 4 }}
                              />
                            </Col>
                            <Col span={12}>
                              <label><strong>Відображувана назва:</strong></label>
                              <Input
                                placeholder="Webhook URL"
                                value={endpoint.label}
                                onChange={(e) => updatePublicEndpoint(index, { label: e.target.value })}
                                style={{ marginTop: 4 }}
                              />
                            </Col>
                            <Col span={12}>
                              <label><strong>Шлях за замовчуванням:</strong></label>
                              <Input
                                placeholder="/webhook"
                                value={endpoint.path}
                                onChange={(e) => {
                                  let value = e.target.value;
                                  // Якщо не починається з "/", додаємо його
                                  if (!value.startsWith('/')) {
                                    value = '/' + value;
                                  }
                                  updatePublicEndpoint(index, { path: value });
                                }}
                                style={{ marginTop: 4 }}
                              />
                            </Col>
                            <Col span={6}>
                              <label><strong>HTTP метод:</strong></label>
                              <Select
                                value={endpoint.method}
                                onChange={(value) => updatePublicEndpoint(index, { method: value })}
                                style={{ width: '100%', marginTop: 4 }}
                              >
                                <Select.Option value="GET">GET</Select.Option>
                                <Select.Option value="POST">POST</Select.Option>
                                <Select.Option value="PUT">PUT</Select.Option>
                                <Select.Option value="DELETE">DELETE</Select.Option>
                              </Select>
                            </Col>
                            <Col span={6}>
                              <label><strong>Обов'язковий:</strong></label>
                              <div style={{ marginTop: 8 }}>
                                <Switch
                                  checked={endpoint.required}
                                  onChange={(checked) => updatePublicEndpoint(index, { required: checked })}
                                />
                              </div>
                            </Col>
                            <Col span={24}>
                              <label><strong>Опис:</strong></label>
                              <Input.TextArea
                                placeholder="Опис призначення цієї публічної точки"
                                value={endpoint.description}
                                onChange={(e) => updatePublicEndpoint(index, { description: e.target.value })}
                                rows={2}
                                style={{ marginTop: 4 }}
                              />
                            </Col>
                          </Row>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              ),
            },
          ]} />

          <Divider />
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCloseTemplateModal}>
                Скасувати
              </Button>
              <Button type="primary" htmlType="submit">
                {editingTemplate ? 'Оновити' : 'Створити'}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>



      {/* Редактор файлів */}
      {isFileEditorVisible && (
      <Modal
        title={`Редагування: ${selectedFile?.fileName}`}
        open
        zIndex={1200}
        onCancel={() => {
          setIsFileEditorVisible(false);
          setSelectedFile(null);
          setFileContent('');
        }}
        destroyOnClose
        width="90%"
        style={{ top: 20 }}
        footer={[
          <Button key="cancel" onClick={() => {
            setIsFileEditorVisible(false);
            setSelectedFile(null);
            setFileContent('');
          }}>
            Скасувати
          </Button>,
          <Button key="save" type="primary" onClick={handleSaveFileContent}>
            Зберегти
          </Button>,
        ]}
      >
        <div style={{ height: '70vh' }}>
          <Editor
            language={selectedFile?.fileType === 'html' ? 'html' : 
                     selectedFile?.fileType === 'css' ? 'css' :
                     selectedFile?.fileType === 'javascript' ? 'javascript' : 'plaintext'}
            value={fileContent}
            onChange={(value) => setFileContent(value || '')}
            theme={monacoTheme}
            options={{
              minimap: { enabled: false },
              wordWrap: 'on',
              fontSize: 14,
            }}
          />
        </div>
      </Modal>
      )}
    </div>
  );
}; 